import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import CasaSectionNav from '@/components/feature/CasaSectionNav';
import { useAuth } from '@/hooks/useAuth';
import FilterBar from './components/FilterBar';
import BookingTable from './components/BookingTable';
import AddBookingModal from './components/AddBookingModal';
import OverrideSourceModal from './components/OverrideSourceModal';
import NoShowModal from './components/NoShowModal';
import ConfirmDialog from './components/ConfirmDialog';

interface RoomInfo {
  name: string;
  room_type: string;
}

interface Booking {
  id: string;
  room_id: string;
  guest_display_name: string;
  arrival_date: string;
  arrival_time: string;
  departure_date: string;
  departure_time: string;
  adults: number;
  children: number;
  infants: number;
  booking_status: string;
  beds24_status: string;
  operational_status: string;
  booking_channel: string;
  source_override: string | null;
  booking_origin: string;
  actual_check_in_at: string | null;
  actual_check_out_at: string | null;
  rooms: RoomInfo | null;
  beds24_booking_financials: { collection_method:string; needs_review:boolean; outstanding_yen:number; invoice_credit_yen:number }[] | null;
}

type FetchStatus = 'idle' | 'loading' | 'success' | 'error';
type OperationStatus = 'idle' | 'loading' | 'success' | 'error';

function getTodayJST(): string {
  const now = new Date();
  const jst = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
  return jst.toISOString().slice(0, 10);
}

function toJSTDate(utcDateStr: string): string {
  const d = new Date(utcDateStr);
  const jst = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
  return jst.toISOString().slice(0, 10);
}

function formatDate(isoStr: string): string {
  const d = new Date(isoStr + 'T00:00:00');
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Asia/Tokyo' });
}

function formatDatetime(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo' });
}

function translateChannel(channel: string): string {
  const map: Record<string, string> = {
    direct: '直接予約',
    airbnb: 'Airbnb',
    booking: 'Booking.com',
    trip: 'Trip.com',
    expedia: 'Expedia',
    phone: '電話',
    walk_in: '飛び込み',
    other: 'その他',
    unknown: '不明',
  };
  return map[channel] || channel;
}

function translateStatus(status: string): string {
  const map: Record<string, string> = {
    expected: '予定',
    checked_in: '滞在中',
    checked_out: 'チェックアウト済',
    no_show: 'ノーショー',
    cancelled: 'キャンセル',
    confirmed: '確定',
    new: '新規',
  };
  return map[status] || status;
}

function translateError(code: string): string {
  const map: Record<string, string> = {
    '22023': '無効な値が含まれています。',
    '23505': '同じ日程の予約が既に存在します。',
    'P0001': '必須項目が不足しています。',
  };
  if (code in map) return map[code];
  if (code.startsWith('P')) return 'データベースエラーが発生しました。';
  return '予期しないエラーが発生しました。もう一度お試しください。';
}

export default function ReservationsPage() {
  const { session } = useAuth();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('idle');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [operationStatus, setOperationStatus] = useState<Record<string, OperationStatus>>({});
  const [operationMessage, setOperationMessage] = useState<string | null>(null);
  const [operationIsError, setOperationIsError] = useState(false);

  const [searchName, setSearchName] = useState('');
  const [searchRoom, setSearchRoom] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('upcoming');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [opStatusFilter, setOpStatusFilter] = useState<string>('');
  const [showPast, setShowPast] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState<Booking | null>(null);
  const [noShowTarget, setNoShowTarget] = useState<Booking | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'check_in' | 'check_out'; booking: Booking } | null>(null);

  const fetchBookings = useCallback(async () => {
    setFetchStatus('loading');
    setFetchError(null);

    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id, room_id, guest_display_name, arrival_date, arrival_time,
          departure_date, departure_time, adults, children, infants,
          booking_status, beds24_status, operational_status,
          booking_channel, source_override, booking_origin,
          actual_check_in_at, actual_check_out_at,
          rooms ( name, room_type ),
          beds24_booking_financials ( collection_method, needs_review, outstanding_yen, invoice_credit_yen )
        `)
        .order('arrival_date', { ascending: true });

      if (error) {
        setFetchStatus('error');
        setFetchError('予約データの取得に失敗しました。');
        return;
      }

      setBookings((data as unknown as Booking[]) || []);
      setFetchStatus('success');
    } catch {
      setFetchStatus('error');
      setFetchError('通信エラーが発生しました。');
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const setRowLoading = (bookingId: string) => {
    setOperationStatus((prev) => ({ ...prev, [bookingId]: 'loading' }));
    setOperationMessage(null);
    setOperationIsError(false);
  };

  const setRowResult = (bookingId: string, isError: boolean) => {
    setOperationStatus((prev) => ({ ...prev, [bookingId]: isError ? 'error' : 'success' }));
  };

  const handleCollectionMethod = async (booking: Booking, method: string) => {
    setRowLoading(booking.id);
    const { error } = await supabase.rpc('admin_set_beds24_collection_method',{p_booking_id:booking.id,p_method:method});
    if(error){ setRowResult(booking.id,true); setOperationIsError(true); setOperationMessage('回収方法を更新できませんでした。'); }
    else { setRowResult(booking.id,false); setOperationIsError(false); setOperationMessage('回収方法を更新しました。'); await fetchBookings(); }
  };

  const handleCheckIn = async (booking: Booking) => {
    setRowLoading(booking.id);
    try {
      const { error } = await supabase.rpc('check_in_booking', { p_booking_id: booking.id });
      if (error) {
        setRowResult(booking.id, true);
        setOperationIsError(true);
        setOperationMessage(translateError(error.code || ''));
        return;
      }
      setRowResult(booking.id, false);
      setOperationIsError(false);
      setOperationMessage('チェックインが完了しました。');
      await fetchBookings();
    } catch {
      setRowResult(booking.id, true);
      setOperationIsError(true);
      setOperationMessage('通信エラーが発生しました。');
    }
  };

  const handleCheckOut = async (booking: Booking) => {
    setRowLoading(booking.id);
    try {
      const { data, error } = await supabase.rpc('check_out_booking', { p_booking_id: booking.id });
      if (error) {
        setRowResult(booking.id, true);
        setOperationIsError(true);
        setOperationMessage(translateError(error.code || ''));
        return;
      }
      setRowResult(booking.id, false);
      setOperationIsError(false);
      const result = data as Record<string, unknown> | null;
      const taskCreated = result?.task_created;
      const msg = taskCreated
        ? 'チェックアウトが完了し、清掃タスクが作成されました。'
        : 'チェックアウトが完了し、既存の清掃タスクが再利用されました。';
      setOperationMessage(msg);
      await fetchBookings();
    } catch {
      setRowResult(booking.id, true);
      setOperationIsError(true);
      setOperationMessage('通信エラーが発生しました。');
    }
  };

  const handleAddBooking = async (formData: {
    room_id: string;
    guest_display_name: string;
    arrival_date: string;
    departure_date: string;
    adults: number;
    booking_channel: string;
    children: number;
    infants: number;
    arrival_time: string;
    departure_time: string;
  }) => {
    try {
      const { error } = await supabase.rpc('create_manual_booking', {
        p_room_id: formData.room_id,
        p_guest_display_name: formData.guest_display_name,
        p_arrival_date: formData.arrival_date,
        p_departure_date: formData.departure_date,
        p_adults: formData.adults,
        p_booking_channel: formData.booking_channel,
        p_children: formData.children,
        p_infants: formData.infants,
        p_arrival_time: formData.arrival_time,
        p_departure_time: formData.departure_time,
      });
      if (error) {
        setOperationIsError(true);
        setOperationMessage(translateError(error.code || ''));
        return false;
      }
      setOperationIsError(false);
      setOperationMessage('予約を追加しました。');
      await fetchBookings();
      return true;
    } catch {
      setOperationIsError(true);
      setOperationMessage('通信エラーが発生しました。');
      return false;
    }
  };

  const handleOverrideSource = async (bookingId: string, source: string, reason: string) => {
    setRowLoading(bookingId);
    try {
      const { error } = await supabase.rpc('override_booking_source', {
        p_booking_id: bookingId,
        p_source_override: source,
        p_reason: reason,
      });
      if (error) {
        setRowResult(bookingId, true);
        setOperationIsError(true);
        setOperationMessage(translateError(error.code || ''));
        return false;
      }
      setRowResult(bookingId, false);
      setOperationIsError(false);
      setOperationMessage('予約経路を訂正しました。');
      await fetchBookings();
      return true;
    } catch {
      setRowResult(bookingId, true);
      setOperationIsError(true);
      setOperationMessage('通信エラーが発生しました。');
      return false;
    }
  };

  const handleNoShow = async (bookingId: string, reason: string) => {
    setRowLoading(bookingId);
    try {
      const { error } = await supabase.rpc('mark_booking_no_show', {
        p_booking_id: bookingId,
        p_reason: reason,
      });
      if (error) {
        setRowResult(bookingId, true);
        setOperationIsError(true);
        setOperationMessage(translateError(error.code || ''));
        return false;
      }
      setRowResult(bookingId, false);
      setOperationIsError(false);
      setOperationMessage('ノーショーとして記録しました。');
      await fetchBookings();
      return true;
    } catch {
      setRowResult(bookingId, true);
      setOperationIsError(true);
      setOperationMessage('通信エラーが発生しました。');
      return false;
    }
  };

  const today = getTodayJST();

  const filteredBookings = bookings.filter((b) => {
    const arrivalJST = toJSTDate(b.arrival_date);
    const departureJST = toJSTDate(b.departure_date);

    if (!showPast && activeFilter !== 'past' && departureJST < today && b.operational_status !== 'checked_in') {
      return false;
    }

    if (searchName && !b.guest_display_name.toLowerCase().includes(searchName.toLowerCase())) {
      return false;
    }
    if (searchRoom && b.rooms && !b.rooms.name.toLowerCase().includes(searchRoom.toLowerCase())) {
      return false;
    }

    switch (activeFilter) {
      case 'today_arrival':
        if (arrivalJST !== today) return false;
        break;
      case 'today_departure':
        if (departureJST !== today) return false;
        break;
      case 'in_house':
        if (b.operational_status !== 'checked_in') return false;
        break;
      case 'upcoming':
        if (arrivalJST <= today) return false;
        break;
      case 'past':
        if (departureJST >= today) return false;
        break;
    }

    if (channelFilter) {
      const displayChannel = b.source_override || b.booking_channel;
      if (displayChannel !== channelFilter) return false;
    }
    if (opStatusFilter && b.operational_status !== opStatusFilter) return false;

    return true;
  });

  const allChannels = [...new Set(bookings.map((b) => b.source_override || b.booking_channel).filter(Boolean))].sort();
  const allOpStatuses = [...new Set(bookings.map((b) => b.operational_status).filter(Boolean))].sort();

  return (
    <div className="max-w-full">
      <CasaSectionNav />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 font-heading">予約管理</h2>
          <p className="text-sm text-foreground-600 mt-1">{filteredBookings.length}件の予約</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </span>
          予約を手動追加
        </button>
      </div>

      {operationMessage && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
          operationIsError
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        }`}>
          {operationMessage}
        </div>
      )}

      <FilterBar
        searchName={searchName}
        setSearchName={setSearchName}
        searchRoom={searchRoom}
        setSearchRoom={setSearchRoom}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        channelFilter={channelFilter}
        setChannelFilter={setChannelFilter}
        opStatusFilter={opStatusFilter}
        setOpStatusFilter={setOpStatusFilter}
        showPast={showPast}
        setShowPast={setShowPast}
        allChannels={allChannels}
        allOpStatuses={allOpStatuses}
        today={today}
      />

      {fetchStatus === 'loading' && (
        <div className="flex items-center justify-center py-20">
          <span className="w-6 h-6 flex items-center justify-center mr-3">
            <i className="ri-loader-4-line text-xl text-primary-500 animate-spin"></i>
          </span>
          <span className="text-sm text-foreground-500">予約を読み込み中...</span>
        </div>
      )}

      {fetchStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl text-red-600"></i>
          </div>
          <p className="text-sm font-medium text-red-700 mb-1">読み込みに失敗しました</p>
          <p className="text-xs text-red-500 mb-4">{fetchError}</p>
          <button
            onClick={fetchBookings}
            className="px-4 py-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium transition-colors cursor-pointer"
          >
            再試行
          </button>
        </div>
      )}

      {fetchStatus === 'success' && filteredBookings.length === 0 && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary-100 flex items-center justify-center">
            <i className="ri-calendar-line text-2xl text-secondary-600"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900 mb-1">該当する予約がありません</h3>
          <p className="text-xs text-foreground-500">検索条件やフィルターを変更してお試しください。</p>
        </div>
      )}

      {fetchStatus === 'success' && filteredBookings.length > 0 && (
        <BookingTable
          bookings={filteredBookings}
          operationStatus={operationStatus}
          today={today}
          toJSTDate={toJSTDate}
          formatDate={formatDate}
          formatDatetime={formatDatetime}
          translateChannel={translateChannel}
          translateStatus={translateStatus}
          onCheckIn={(b) => setConfirmAction({ type: 'check_in', booking: b })}
          onCheckOut={(b) => setConfirmAction({ type: 'check_out', booking: b })}
          onOverrideSource={setOverrideTarget}
          onNoShow={setNoShowTarget}
          onCollectionMethod={handleCollectionMethod}
        />
      )}

      {showAddModal && (
        <AddBookingModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddBooking}
        />
      )}

      {overrideTarget && (
        <OverrideSourceModal
          booking={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onSubmit={handleOverrideSource}
        />
      )}

      {noShowTarget && (
        <NoShowModal
          bookingName={noShowTarget.guest_display_name}
          roomName={noShowTarget.rooms?.name || '未割当'}
          onClose={() => setNoShowTarget(null)}
          onSubmit={(reason) => handleNoShow(noShowTarget.id, reason).then(() => undefined)}
        />
      )}

      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.type === 'check_in' ? 'チェックイン確認' : 'チェックアウト確認'}
          message={
            confirmAction.type === 'check_in'
              ? `${confirmAction.booking.guest_display_name} をチェックインしますか？`
              : `${confirmAction.booking.guest_display_name} をチェックアウトしますか？`
          }
          confirmLabel={confirmAction.type === 'check_in' ? 'チェックイン' : 'チェックアウト'}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => {
            const action = confirmAction;
            setConfirmAction(null);
            if (action.type === 'check_in') handleCheckIn(action.booking);
            else handleCheckOut(action.booking);
          }}
        />
      )}
    </div>
  );
}

export { getTodayJST, toJSTDate, formatDate, formatDatetime, translateChannel, translateStatus };
